import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
// Question data
const QUESTIONS = [{
  id: 1,
  question: 'What kind of situation was this?',
  options: ['Conflict / disagreement', 'Decision / choice', 'Review / feedback', 'Coordination / alignment', 'Authority / hierarchy', 'Unclear / ambiguous']
}, {
  id: 2,
  question: 'What role were you mostly in?',
  options: ['Driving it forward', 'Responding to others', 'Mediating / bridging', 'Observing from the side', 'Blocked or constrained']
}, {
  id: 3,
  question: 'What was the main tension?',
  options: ['Speed vs quality', 'Control vs trust', 'Harmony vs truth', 'Ownership vs alignment', 'Risk vs safety', 'Recognition vs invisibility']
}, {
  id: 4,
  question: 'How did it end (for now)?',
  options: ['I pushed it through', 'We postponed or deferred', 'Someone else decided', 'It dissolved or faded', "It's still unresolved"]
}];
export function WorkMirrorSituationDecode() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [optionalContext, setOptionalContext] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const totalSteps = 5; // 4 questions + 1 optional context
  const handleOptionSelect = (option: string) => {
    // Save answer
    setAnswers({
      ...answers,
      [currentStep]: option
    });
    // Auto-advance to next screen
    if (currentStep < 4) {
      setTimeout(() => {
        setCurrentStep(currentStep + 1);
      }, 150);
    } else {
      // Move to optional context screen
      setTimeout(() => {
        setCurrentStep(5);
      }, 150);
    }
  };
  const handleSave = () => {
    // Save the decode with optional context
    console.log('Saving situation decode:', {
      answers,
      optionalContext
    });
    // Show confirmation
    setShowConfirmation(true);
    // Return to app home after brief delay
    setTimeout(() => {
      navigate('/work-mirror/app');
    }, 1500);
  };
  const handleCancel = () => {
    navigate('/work-mirror/app');
  };
  // Confirmation screen
  if (showConfirmation) {
    return <>
        <Helmet data-id="element-4363">
          <title data-id="element-4364">Saved | Work Mirror</title>
          <meta name="robots" content="noindex, nofollow" data-id="element-4365" />
        </Helmet>

        <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex items-center justify-center px-6" data-id="element-4366">
          <div className="text-center" data-id="element-4367">
            <p className="text-xl text-[#F5F5F5]" data-id="element-4368">Saved to your mirror.</p>
          </div>
        </div>
      </>;
  }
  // Optional context screen (step 5)
  if (currentStep === 5) {
    return <>
        <Helmet data-id="element-4369">
          <title data-id="element-4370">Decode Situation | Work Mirror</title>
          <meta name="robots" content="noindex, nofollow" data-id="element-4371" />
        </Helmet>

        <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-4372">
          {/* Header */}
          <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A] border-b border-[#2B2B2B]" data-id="element-4373">
            <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-4374">
              <button onClick={handleCancel} className="text-sm text-gray-500 hover:text-gray-400 transition-colors" data-id="element-4375">
                Cancel
              </button>
              <p className="text-xs font-mono text-gray-600" data-id="element-4376">
                {currentStep} of {totalSteps}
              </p>
              <div className="w-16" data-id="element-4377" /> {/* Spacer for alignment */}
            </div>
          </header>

          <main className="pt-24 pb-16 px-6" data-id="element-4378">
            <div className="max-w-2xl mx-auto space-y-12" data-id="element-4379">
              {/* Title */}
              <div className="space-y-4 pt-8" data-id="element-4380">
                <h1 className="text-2xl md:text-3xl font-light text-[#F5F5F5] leading-relaxed" data-id="element-4381">
                  Anything to add? (Optional)
                </h1>
                <p className="text-base text-gray-500" data-id="element-4382">
                  One line of context, if you want.
                </p>
              </div>

              {/* Input */}
              <div data-id="element-4383">
                <input type="text" value={optionalContext} onChange={e => setOptionalContext(e.target.value)} placeholder="Weekly review with product team." className="w-full p-6 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-base placeholder:text-gray-700 focus:outline-none focus:border-[#64748B] transition-colors" autoFocus data-id="element-4384" />
              </div>

              {/* Save button */}
              <div className="pt-8" data-id="element-4385">
                <button onClick={handleSave} className="px-10 py-4 bg-[#F5F5F5] text-[#0A0A0A] font-medium text-sm hover:bg-gray-200 transition-colors" data-id="element-4386">
                  Save
                </button>
              </div>
            </div>
          </main>
        </div>
      </>;
  }
  // Question screens (steps 1-4)
  const currentQuestion = QUESTIONS[currentStep - 1];
  return <>
      <Helmet data-id="element-4387">
        <title data-id="element-4388">Decode Situation | Work Mirror</title>
        <meta name="robots" content="noindex, nofollow" data-id="element-4389" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-4390">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A] border-b border-[#2B2B2B]" data-id="element-4391">
          <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-4392">
            <button onClick={handleCancel} className="text-sm text-gray-500 hover:text-gray-400 transition-colors" data-id="element-4393">
              Cancel
            </button>
            <p className="text-xs font-mono text-gray-600" data-id="element-4394">
              {currentStep} of {totalSteps}
            </p>
            <div className="w-16" data-id="element-4395" /> {/* Spacer for alignment */}
          </div>
        </header>

        <main className="pt-24 pb-16 px-6" data-id="element-4396">
          <div className="max-w-2xl mx-auto" data-id="element-4397">
            {/* Question */}
            <div className="mb-12 pt-8" data-id="element-4398">
              <h1 className="text-2xl md:text-3xl font-light text-[#F5F5F5] leading-relaxed" data-id="element-4399">
                {currentQuestion.question}
              </h1>
            </div>

            {/* Options */}
            <div className="space-y-3" data-id="element-4400">
              {currentQuestion.options.map((option, index) => <button key={index} onClick={() => handleOptionSelect(option)} className="w-full text-left p-6 border border-[#2B2B2B] hover:border-[#64748B] hover:bg-[#0A0A0A]/50 transition-all text-base text-[#F5F5F5] leading-relaxed" data-id="element-4401">
                  {option}
                </button>)}
            </div>
          </div>
        </main>
      </div>
    </>;
}